import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, Icon } from '..';

export interface StepProps {
  icon: any;
  header: string;
  subtext?: string;
}

export interface VerticalVariantProps {
  activeColor?: string;
  completedCheckboxcolor?: string;
  width?: string;
}

export interface WizardMenuProps {
  direction: 'horizontal' | 'vertical' | 'verticalVariant';
  steps: StepProps[];
  inProgress: number;
  size?: 'small';
  displayStepNumbers?: boolean;
  verticalVariantProps?: VerticalVariantProps;
}

export const WizardStepMenu = ({
  direction,
  steps,
  inProgress,
  size,
  displayStepNumbers = true,
  verticalVariantProps,
}: WizardMenuProps) => {
  /* Checking for missing required props */
  if (!direction)
    throw new Error(
      `You need to pass a prop called "direction" of type string to the WizardStepMenu component. To learn more about the list of required props for the
      WizardStepMenu component go to Maverick documentation at: https://thryvlabs-maverick.netlify.app/?path=/docs/ui-wizard-step-menus`
    );
  if (!steps)
    throw new Error(
      `You need to pass a prop called "steps" of type array to the WizardStepMenu component. To learn more about the list of required props for the
      WizardStepMenu component go to Maverick documentation at: https://thryvlabs-maverick.netlify.app/?path=/docs/ui-wizard-step-menus`
    );
  if (!inProgress) {
    if (typeof inProgress !== 'number' && typeof inProgress !== 'undefined') {
      throw new Error(
        `You need to pass a prop called "inProgress" of type number to the WizardStepMenu component. To learn more about the list of required props for the
        WizardStepMenu component go to Maverick documentation at: https://thryvlabs-maverick.netlify.app/?path=/docs/ui-wizard-step-menus`
      );
    }
  }
  /* Checking for missing required props */

  // These progressBarStyles are to color the progressbar
  let progressBarStyles = {};
  {
    typeof inProgress === 'number'
      ? direction === 'horizontal'
        ? (progressBarStyles = {
            background: `linear-gradient(
        to right, 
        #FF5000,
        #FF5000 ${
          ((inProgress + 1) * 100) / steps.length - 100 / (steps.length * 2)
        }%,
        #A3A5A7 ${
          ((inProgress + 1) * 100) / steps.length - 100 / (steps.length * 2)
        }%,
        #A3A5A7 ${
          100 -
          (((inProgress + 1) * 100) / steps.length - 100 / (steps.length * 2))
        }%
      )`,
          })
        : (progressBarStyles = {
            background: `linear-gradient(
        #FF5000,
        #FF5000 ${
          ((inProgress + 1) * 100) / steps.length - 100 / (steps.length * 2)
        }%,
        #A3A5A7 ${
          ((inProgress + 1) * 100) / steps.length - 100 / (steps.length * 2)
        }%,
        #A3A5A7 ${
          100 -
          (((inProgress + 1) * 100) / steps.length - 100 / (steps.length * 2))
        }%
      )`,
          })
      : (progressBarStyles = { background: '#A3A5A7' });
  }

  /* ---- This is creating and modifying copies of the props that were passed with <Icon> so we can change the color of the icon based on progress. ---- */

  const [iconSize, setIconSize] = useState<number>(49);
  // props is used to modify the size and color of the icons
  const [props, setProps] = useState<StepProps[]>(
    steps.map((step, i) => {
      if (inProgress === i) {
        return {
          ...step.icon.props,
          color: '#FF5000',
        };
      } else return { ...step.icon.props };
    })
  );
  const [changedSteps, setChangedSteps] = useState(steps || []);
  const horizontalNav = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (direction === 'horizontal' && !size) {
      const handleResizeIcons = () => {
        if (
          horizontalNav?.current?.clientWidth &&
          horizontalNav?.current?.clientWidth / changedSteps.length < 49
        ) {
          const size = Math.floor(
            horizontalNav?.current?.clientWidth / changedSteps.length
          );
          setIconSize(size);
          setProps(
            steps.map((step, i) => {
              if (inProgress === i) {
                return {
                  ...step.icon.props,
                  color: '#FF5000',
                  height: `${size / 2}px`,
                  width: `${size / 2}px`,
                };
              } else
                return {
                  ...step.icon.props,
                  height: `${size / 2}px`,
                  width: `${size / 2}px`,
                };
            })
          );
        }
      };

      handleResizeIcons();

      window.addEventListener('resize', handleResizeIcons);

      return () => {
        window.removeEventListener('resize', handleResizeIcons);
      };
    }
    return;
  }, []);

  useEffect(() => {
    setProps(
      steps.map((step, i) => {
        if (inProgress === i) {
          return {
            ...step.icon.props,
            color:
              direction === 'verticalVariant'
                ? verticalVariantProps?.activeColor || '#056E30'
                : '#FF5000',
          };
        } else if (inProgress < i && direction === 'verticalVariant') {
          return {
            ...step.icon.props,
            color: '#A3A5A7',
          };
        }
        return { ...step.icon.props };
      })
    );
  }, [inProgress]);

  useEffect(() => {
    setChangedSteps([
      ...steps.map((step, i) => {
        return {
          header: step.header,
          icon: {
            ...step.icon,
            props: props[i],
          },
          subtext: step.subtext,
        };
      }),
    ]);
  }, [props]);

  const renderHorizontalMenu = direction === 'horizontal' && !size && (
    <div className={`flex flex-col justify-center relative`}>
      {/* This is the bar portion of the wizard */}
      <div
        className="h-[4px] w-full absolute"
        style={{ ...progressBarStyles, top: iconSize / 2 - 4 }}
      />
      <div className="flex justify-between z-10  w-full" ref={horizontalNav}>
        {changedSteps.map((step, i) => (
          <div
            className={`items-center text-center flex flex-col min-w-min`}
            style={{ width: `calc(100%/${changedSteps.length})` }}
            key={i}
          >
            {inProgress > i ? (
              <CheckIcon height={`${iconSize}`} width={`${iconSize}`} />
            ) : (
              <div
                className={
                  inProgress === i
                    ? 'bg-white rounded-full flex justify-center items-center border-2 border-thryv-orange-500'
                    : 'bg-thryv-gray-medium-500  rounded-full flex justify-center items-center'
                }
                style={{ height: `${iconSize}px`, width: `${iconSize}px` }}
              >
                <div className="">{step.icon}</div>
              </div>
            )}

            <div className={`flex flex-col x-sm:hidden`}>
              <span className="font-montserrat font-semibold text-h8">
                {displayStepNumbers && `${i + 1}.`} {step.header}
              </span>
              {step.subtext ? (
                <p className="font-open-sans text-thryv-neutral text-p-sm">
                  {step.subtext}
                </p>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderVerticalMenu = direction === 'vertical' && !size && (
    <div className={`flex relative max-w-max m-0`}>
      {/* This is the bar portion of the wizard */}
      <div
        className="h-full w-[4px] absolute left-[23px]"
        style={progressBarStyles}
      ></div>
      <div className="flex flex-col z-10 pt-[50px]">
        {changedSteps.map((step, i) => (
          <div
            className={`flex items-center mb-[50px]`}
            style={{ height: `calc(100%/${changedSteps.length})` }}
            key={i}
          >
            {inProgress > i ? (
              <CheckIcon className="mx-0 mr-[12px]" />
            ) : (
              <div
                className={
                  inProgress === i
                    ? 'bg-white h-[49px] w-[49px] rounded-full flex justify-center items-center border-2 border-thryv-orange-500 mr-[12px]'
                    : 'bg-thryv-gray-medium-500 h-[49px] w-[49px] rounded-full flex justify-center items-center mr-[12px]'
                }
              >
                <div className="">{step.icon}</div>
              </div>
            )}

            <div className={`flex flex-col`}>
              <span className="font-montserrat font-semibold text-h8">
                {displayStepNumbers && `${i + 1}.`} {step.header}
              </span>
              {step.subtext ? (
                <p className="font-open-sans text-thryv-neutral text-p-sm">
                  {step.subtext}
                </p>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const checkSizeAndDirection = direction && size && (
    <div>
      {size !== 'small' ? (
        <div>** &apos;small&apos; is the only accepted size prop **</div>
      ) : direction !== 'vertical' ? (
        <div>
          ** Direction must be &apos;vertical&apos; to use
          size=&apos;small&apos; **
        </div>
      ) : null}
    </div>
  );

  const renderSmallVerticalMenu = direction === 'vertical' &&
    size === 'small' && (
      <div className={`flex relative max-w-max m-0`}>
        {/* This is the bar portion of the wizard */}
        <div
          className="h-full w-[4px] absolute left-[14px]"
          style={progressBarStyles}
        ></div>
        <div className="flex flex-col z-10 pt-[24px]">
          {changedSteps.map((step, i) => (
            <div
              className={`flex items-center mb-[24px]`}
              style={{ height: `calc(100%/${changedSteps.length})` }}
              key={i}
            >
              {inProgress > i ? (
                <CheckIcon className="mx-0 mr-[12px]" size="small" />
              ) : (
                <div
                  className={
                    inProgress === i
                      ? 'bg-white h-[32px] w-[32px] rounded-full flex justify-center items-center border-2 border-thryv-orange-500 mr-[12px]'
                      : 'bg-thryv-gray-medium-500 h-[32px] w-[32px] rounded-full flex justify-center items-center mr-[12px]'
                  }
                >
                  <div className="">{step.icon}</div>
                </div>
              )}
              <div className={`flex flex-col`}>
                <span className="font-montserrat font-semibold text-h9">
                  {displayStepNumbers && `${i + 1}.`} {step.header}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );

  const renderVerticalVariant =
    direction === 'verticalVariant' ? (
      <div
        className={`flex-col max-w-max space-y-1`}
        style={{ maxWidth: verticalVariantProps?.width || '' }}
      >
        {changedSteps?.map((step, i) => (
          <div
            key={i}
            className={`flex justify-between items-center py-2 px-4 min-h-[55px] ${
              inProgress === i
                ? `border border-solid border-[#E3E6E8] rounded-[4px] bg-[#FFFFFF] shadow-thryv `
                : ''
            }`}
          >
            <div className={`flex justify-start items-center p-1 gap-2.5`}>
              {step?.icon}
              <p
                className={`text-base font-montserrat font-semibold text-[14px]`}
                style={{
                  color:
                    inProgress === i
                      ? verticalVariantProps?.activeColor || '#056E30'
                      : inProgress < i
                      ? '#A3A5A7'
                      : 'black',
                }}
              >
                {step?.header}
              </p>
            </div>
            {inProgress > i ? (
              <div
                className={`flex justify-center items-center w-[18px] h-[18px] rounded-[50%] ml-4`}
                style={{
                  background:
                    verticalVariantProps?.completedCheckboxcolor || '#16A085',
                }}
              >
                <Icon type="solid" variant="check" color="#FFF" width="10px" />
              </div>
            ) : (
              <div
                className={`flex justify-center items-center
               ${
                 inProgress === i ? 'text-white' : 'text-black'
               } w-[18px] h-[18px] rounded-[50%] text-[12px] font-open-sans font-bold ml-12`}
                style={{
                  background:
                    inProgress === i
                      ? verticalVariantProps?.activeColor || `#056E30`
                      : '#E3E6E8',
                }}
              >
                {i + 1}
              </div>
            )}
          </div>
        ))}
      </div>
    ) : null;

  return (
    <div>
      {renderHorizontalMenu}
      {renderVerticalMenu}
      {renderSmallVerticalMenu}
      {checkSizeAndDirection}
      {renderVerticalVariant}
    </div>
  );
};
